import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Stack,
    IconButton,
    Divider,
    Avatar,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import { MuiTelInput } from 'mui-tel-input';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import QuotaBadge from '../components/QuotaBadge';
import Chip from '@mui/material/Chip';
import { FormField, FormSection, arborGreen, filledInputSx } from '../components/FormDialog';
import { useTranslation } from 'react-i18next';

const creamBg = '#F3F2EE';

function Profile() {
    const { t } = useTranslation();
    const { api, logout } = useAuth();
    const navigate = useNavigate();

    // Profile form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [profileSuccess, setProfileSuccess] = useState('');

    // Password form state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Page loading state
    const [pageLoading, setPageLoading] = useState(true);
    const [email, setEmail] = useState('');

    // 2FA state
    const [totpEnabled, setTotpEnabled] = useState(false);
    const [email2faEnabled, setEmail2faEnabled] = useState(false);
    const [twoFaLoading, setTwoFaLoading] = useState(false);
    const [twoFaError, setTwoFaError] = useState('');
    const [twoFaSuccess, setTwoFaSuccess] = useState('');

    // Close account state
    const [closeAccountOpen, setCloseAccountOpen] = useState(false);
    const [closeAccountPassword, setCloseAccountPassword] = useState('');
    const [closeAccountLoading, setCloseAccountLoading] = useState(false);
    const [closeAccountError, setCloseAccountError] = useState('');

    const handleCloseAccount = async () => {
        setCloseAccountLoading(true);
        setCloseAccountError('');
        try {
            await api.delete('/users/me', { data: { password: closeAccountPassword } });
            logout();
        } catch (err) {
            setCloseAccountError(err.response?.data?.detail || t('Failed to close account. Please check your password.'));
        } finally {
            setCloseAccountLoading(false);
        }
    };

    // TOTP setup dialog state
    const [totpDialogOpen, setTotpDialogOpen] = useState(false);
    const [totpQrCode, setTotpQrCode] = useState('');
    const [totpSecret, setTotpSecret] = useState('');
    const [totpVerifyCode, setTotpVerifyCode] = useState('');
    const [totpSetupLoading, setTotpSetupLoading] = useState(false);
    const [totpSetupError, setTotpSetupError] = useState('');

    // Disable 2FA confirmation dialog state
    const [disable2faType, setDisable2faType] = useState(null); // 'totp' | 'email'
    const [disable2faPassword, setDisable2faPassword] = useState('');
    const [disable2faError, setDisable2faError] = useState('');
    const [disable2faLoading, setDisable2faLoading] = useState(false);

    const [serviceTier, setServiceTier] = useState('FREE');

    // Fetch profile data from backend on mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await api.get('/users/me');
                const user = response.data;
                setFirstName(user.first_name || '');
                setLastName(user.last_name || '');
                setPhoneNumber(user.phone_number || '');
                setEmail(user.email || '');
                setServiceTier(user.service_tier || 'FREE');
            } catch (err) {
                setProfileError(t('Failed to load profile data'));
            } finally {
                setPageLoading(false);
            }
        };
        fetchProfile();

    }, [api, t]);

    // Fetch 2FA status
    useEffect(() => {
        const fetch2faStatus = async () => {
            try {
                const response = await api.get('/2fa/status');
                setTotpEnabled(response.data.totp_enabled);
                setEmail2faEnabled(response.data.email_2fa_enabled);
            } catch (err) {
                console.error('Failed to fetch 2FA status', err);
            }
        };
        fetch2faStatus();
    }, [api]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileLoading(true);
        setProfileError('');
        setProfileSuccess('');

        if (phoneNumber && phoneNumber.replace(/\D/g, '').length < 10) {
            setProfileError(t('Invalid phone number format. Please enter at least 10 digits.'));
            setProfileLoading(false);
            return;
        }

        try {
            await api.put('/users/me', {
                first_name: firstName,
                last_name: lastName,
                phone_number: phoneNumber
            });
            setProfileSuccess(t('Profile updated successfully'));
        } catch (err) {
            setProfileError(err.response?.data?.detail || t('Failed to update profile'));
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        if (newPassword !== confirmPassword) {
            setPasswordError(t('New passwords do not match'));
            setPasswordLoading(false);
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError(t('New password must be at least 6 characters'));
            setPasswordLoading(false);
            return;
        }

        try {
            await api.put('/users/me/password', {
                current_password: currentPassword,
                new_password: newPassword
            });
            setPasswordSuccess(t('Password changed successfully'));
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPasswordError(err.response?.data?.detail || t('Failed to change password'));
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleToggleTotp = async () => {
        if (totpEnabled) {
            setDisable2faType('totp');
            setDisable2faPassword('');
            setDisable2faError('');
        } else {
            setTotpSetupError('');
            setTotpVerifyCode('');
            setTotpSetupLoading(true);
            try {
                const response = await api.post('/2fa/totp/setup');
                setTotpQrCode(response.data.qr_code);
                setTotpSecret(response.data.secret);
                setTotpDialogOpen(true);
            } catch (err) {
                setTwoFaError(err.response?.data?.detail || t('Failed to start authenticator setup'));
            } finally {
                setTotpSetupLoading(false);
            }
        }
    };

    const handleConfirmDisable2fa = async () => {
        if (!disable2faPassword) return;
        setDisable2faLoading(true);
        setDisable2faError('');
        try {
            if (disable2faType === 'totp') {
                await api.put('/2fa/totp/disable', { password: disable2faPassword });
                setTotpEnabled(false);
                setTwoFaSuccess(t('Authenticator app disabled'));
            } else if (disable2faType === 'email') {
                await api.put('/2fa/email/disable', { password: disable2faPassword });
                setEmail2faEnabled(false);
                setTwoFaSuccess(t('Email verification disabled'));
            }
            setDisable2faType(null);
            setDisable2faPassword('');
        } catch (err) {
            setDisable2faError(err.response?.data?.detail || t('Failed to disable 2FA'));
        } finally {
            setDisable2faLoading(false);
        }
    };

    const handleVerifyTotpSetup = async () => {
        setTotpSetupLoading(true);
        setTotpSetupError('');
        try {
            await api.post('/2fa/totp/verify-setup', { code: totpVerifyCode });
            setTotpEnabled(true);
            setTotpDialogOpen(false);
            setTwoFaSuccess(t('Authenticator app enabled successfully'));
        } catch (err) {
            setTotpSetupError(err.response?.data?.detail || t('Invalid code. Please try again.'));
        } finally {
            setTotpSetupLoading(false);
        }
    };

    const handleToggleEmail2fa = async () => {
        if (email2faEnabled) {
            setDisable2faType('email');
            setDisable2faPassword('');
            setDisable2faError('');
        } else {
            setTwoFaLoading(true);
            setTwoFaError('');
            setTwoFaSuccess('');
            try {
                await api.put('/2fa/email/enable');
                setEmail2faEnabled(true);
                setTwoFaSuccess(t('Email verification enabled'));
            } catch (err) {
                setTwoFaError(err.response?.data?.detail || t('Failed to update email verification settings'));
            } finally {
                setTwoFaLoading(false);
            }
        }
    };

    const initials = firstName || lastName
        ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
        : email?.charAt(0)?.toUpperCase() || '?';

    if (pageLoading) {
        return (
            <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 4, mb: 8, maxWidth: 800, mx: 'auto', px: 2 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
                <IconButton 
                    onClick={() => navigate('/')} 
                    sx={{ 
                        mr: 2, 
                        bgcolor: 'rgba(10, 42, 33, 0.05)',
                        color: arborGreen,
                        '&:hover': { bgcolor: 'rgba(10, 42, 33, 0.1)' }
                    }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, color: arborGreen, letterSpacing: '-0.02em' }}>
                        {t('Account Settings')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.6)' }}>
                        {t('Manage your personal information and security preferences.')}
                    </Typography>
                </Box>
            </Box>

            {/* User Header Card */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4,
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 3, 
                    bgcolor: creamBg,
                    borderRadius: '32px',
                    border: '1px solid rgba(10, 42, 33, 0.05)',
                }}
            >
                <Avatar 
                    sx={{ 
                        width: 80, 
                        height: 80, 
                        bgcolor: arborGreen, 
                        fontSize: '2rem', 
                        fontWeight: 600,
                        boxShadow: '0 8px 24px rgba(10, 42, 33, 0.2)'
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: arborGreen }}>
                        {t('Plan:')}
                    </Typography>
                    <Chip
                        label={t(serviceTier) || serviceTier}
                        color={serviceTier === 'ADMIN' ? 'secondary' : serviceTier === 'PRO' ? 'primary' : 'default'}
                        variant={serviceTier === 'FREE' ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 700, px: 1 }}
                    />
                </Box>
            </Paper>

            {/* Service Tier & Quotas Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4, 
                    borderRadius: '32px',
                    border: '1px solid rgba(10, 42, 33, 0.05)',
                }}
            >
                <FormSection title={t('Plan & Feature Usage')}>
                    <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.6)', mb: 3 }}>
                        {t('Your current service plan limits and feature usage quotas.')}
                    </Typography>
                    <QuotaBadge featureKey="ai_document_scan" />
                </FormSection>
            </Paper>


            {/* Profile Information Form Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4, 
                    borderRadius: '32px',
                    border: '1px solid rgba(10, 42, 33, 0.05)',
                }}
            >
                <FormSection title={t('Profile Information')}>
                    {profileError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{profileError}</Alert>}
                    {profileSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{profileSuccess}</Alert>}

                    <Box component="form" onSubmit={handleProfileUpdate}>
                        <Stack spacing={4}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <FormField label={t('First Name')} id="firstName" required sx={{ flex: 1 }}>
                                    <TextField
                                        id="firstName"
                                        variant="filled"
                                        fullWidth
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        disabled={profileLoading}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                                <FormField label={t('Last Name')} id="lastName" required sx={{ flex: 1 }}>
                                    <TextField
                                        id="lastName"
                                        variant="filled"
                                        fullWidth
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        disabled={profileLoading}
                                        sx={filledInputSx}
                                    />
                                </FormField>
                            </Stack>

                            <FormField label={t('Phone Number')} id="phoneNumber">
                                <MuiTelInput
                                    id="phoneNumber"
                                    variant="filled"
                                    fullWidth
                                    value={phoneNumber}
                                    onChange={(value) => setPhoneNumber(value)}
                                    disabled={profileLoading}
                                    defaultCountry="US"
                                    preferredCountries={['US', 'CA']}
                                    sx={filledInputSx}
                                />
                            </FormField>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={profileLoading}
                                    startIcon={profileLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                    disableElevation
                                    sx={{ 
                                        py: 1.5, 
                                        px: 4, 
                                        borderRadius: 1, 
                                        bgcolor: arborGreen,
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: '#061a15' }
                                    }}
                                >
                                    {profileLoading ? t('Saving...') : t('Save Changes')}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </FormSection>
            </Paper>

            {/* Security Section (Password) */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4, 
                    borderRadius: '32px',
                    border: '1px solid rgba(10, 42, 33, 0.05)',
                }}
            >
                <FormSection title={t('Security')}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: arborGreen, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LockIcon fontSize="small" /> {t('Change Password')}
                        </Typography>
                    </Box>

                    {passwordError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{passwordError}</Alert>}
                    {passwordSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{passwordSuccess}</Alert>}

                    <Box component="form" onSubmit={handlePasswordChange}>
                        <Stack spacing={4}>
                            <FormField label={t('Current Password')} id="currentPassword">
                                <TextField
                                    id="currentPassword"
                                    variant="filled"
                                    type="password"
                                    fullWidth
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    disabled={passwordLoading}
                                    autoComplete="current-password"
                                    sx={filledInputSx}
                                />
                            </FormField>
                            
                            <Divider sx={{ opacity: 0.4 }} />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <FormField label={t('New Password')} id="newPassword" sx={{ flex: 1 }}>
                                    <TextField
                                        id="newPassword"
                                        variant="filled"
                                        type="password"
                                        fullWidth
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        disabled={passwordLoading}
                                        autoComplete="new-password"
                                        sx={filledInputSx}
                                        helperText={t('Must be at least 6 characters')}
                                    />
                                </FormField>
                                <FormField label={t('Confirm New Password')} id="confirmPassword" sx={{ flex: 1 }}>
                                    <TextField
                                        id="confirmPassword"
                                        variant="filled"
                                        type="password"
                                        fullWidth
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={passwordLoading}
                                        autoComplete="new-password"
                                        error={confirmPassword.length > 0 && newPassword.length > 0 && confirmPassword !== newPassword}
                                        sx={filledInputSx}
                                        helperText={confirmPassword.length > 0 && newPassword.length > 0 && confirmPassword !== newPassword ? t('Passwords do not match') : ''}
                                    />
                                </FormField>
                            </Stack>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                                    startIcon={passwordLoading ? <CircularProgress size={20} color="inherit" /> : null}
                                    disableElevation
                                    sx={{ 
                                        py: 1.5, 
                                        px: 4, 
                                        borderRadius: 1, 
                                        bgcolor: arborGreen,
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: '#061a15' }
                                    }}
                                >
                                    {passwordLoading ? t('Updating...') : t('Update Password')}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                </FormSection>
            </Paper>

            {/* Two-Factor Authentication Section */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 4, 
                    mb: 4,
                    borderRadius: '32px',
                    border: '1px solid rgba(10, 42, 33, 0.05)',
                }}
            >
                <FormSection title={t('Two-Factor Authentication')}>
                    <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.6)', mb: 4 }}>
                        {t('Add an extra layer of security to your account by requiring a verification code.')}
                    </Typography>

                    {twoFaError && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{twoFaError}</Alert>}
                    {twoFaSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{twoFaSuccess}</Alert>}

                    <Stack spacing={3}>
                        <Box 
                            sx={{
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                p: 3, 
                                borderRadius: 3, 
                                bgcolor: totpEnabled ? 'rgba(10, 42, 33, 0.03)' : 'transparent',
                                border: '1px solid', 
                                borderColor: totpEnabled ? 'rgba(10, 42, 33, 0.1)' : 'rgba(10, 42, 33, 0.05)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ 
                                    width: 48, 
                                    height: 48, 
                                    borderRadius: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: totpEnabled ? arborGreen : 'rgba(10, 42, 33, 0.05)',
                                    color: totpEnabled ? '#fff' : 'rgba(10, 42, 33, 0.4)'
                                }}>
                                    <PhonelinkLockIcon />
                                </Box>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: arborGreen }}>
                                        {t('Authenticator App')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.5)' }}>
                                        {t('Google Authenticator, Authy, or similar')}
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={totpEnabled}
                                onChange={handleToggleTotp}
                                disabled={twoFaLoading || totpSetupLoading}
                                color="primary"
                            />
                        </Box>

                        <Box 
                            sx={{
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                p: 3, 
                                borderRadius: 3, 
                                bgcolor: email2faEnabled ? 'rgba(10, 42, 33, 0.03)' : 'transparent',
                                border: '1px solid', 
                                borderColor: email2faEnabled ? 'rgba(10, 42, 33, 0.1)' : 'rgba(10, 42, 33, 0.05)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={{ 
                                    width: 48, 
                                    height: 48, 
                                    borderRadius: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: email2faEnabled ? arborGreen : 'rgba(10, 42, 33, 0.05)',
                                    color: email2faEnabled ? '#fff' : 'rgba(10, 42, 33, 0.4)'
                                }}>
                                    <EmailIcon />
                                </Box>
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: arborGreen }}>
                                        {t('Email Verification')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.5)' }}>
                                        {t('Receive a verification code at')} {email}
                                    </Typography>
                                </Box>
                            </Box>
                            <Switch
                                checked={email2faEnabled}
                                onChange={handleToggleEmail2fa}
                                disabled={twoFaLoading}
                                color="primary"
                            />
                        </Box>
                    </Stack>
                </FormSection>
            </Paper>

            {/* Danger Zone Section */}
            <Paper
                elevation={0}
                sx={{
                    mt: 8,
                    p: 4,
                    borderRadius: '32px',
                    border: '1px solid rgba(64, 27, 27, 0.15)',
                    bgcolor: 'rgba(64, 27, 27, 0.02)',
                }}
            >
                <FormSection title={t('Account Termination')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <DeleteForeverIcon sx={{ color: '#401B1B', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#401B1B' }}>
                            {t('Danger Zone')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(64, 27, 27, 0.6)', mb: 4 }}>
                        {t('Permanently close your account and delete all associated data. This action')} <strong>{t('cannot be undone')}</strong>.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setCloseAccountPassword('');
                            setCloseAccountError('');
                            setCloseAccountOpen(true);
                        }}
                        sx={{
                            borderColor: 'rgba(64, 27, 27, 0.3)',
                            color: '#401B1B',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 1, 
                            px: 3,
                            '&:hover': {
                                bgcolor: 'rgba(64, 27, 27, 0.05)',
                                borderColor: '#401B1B',
                            },
                        }}
                    >
                        {t('Close My Account')}
                    </Button>
                </FormSection>
            </Paper>

            {/* TOTP Setup Dialog */}
            <Dialog open={totpDialogOpen} onClose={totpSetupLoading ? undefined : () => setTotpDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{t("Set Up Authenticator App")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Scan the QR code below with your authenticator app, then enter the 6-digit code to verify.")}
                        </Typography>
                        {totpQrCode && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <img
                                    src={`data:image/png;base64,${totpQrCode}`}
                                    alt="TOTP QR Code"
                                    style={{ width: 200, height: 200, borderRadius: 12 }}
                                />
                            </Box>
                        )}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                                {t("Manual key:")}
                            </Typography>
                            <Typography variant="body2" sx={{
                                fontFamily: 'monospace', fontWeight: 600, mt: 0.5,
                                bgcolor: 'action.hover', p: 1, borderRadius: 1, letterSpacing: 2
                            }}>
                                {totpSecret}
                            </Typography>
                        </Box>
                        {totpSetupError && <Alert severity="error">{totpSetupError}</Alert>}
                        <TextField
                            autoFocus
                            label={t("Verification Code")}
                            fullWidth
                            variant="filled"
                            slotProps={{
                                htmlInput: { inputMode: 'numeric' }
                            }}
                            value={totpVerifyCode}
                            onChange={(e) => setTotpVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            disabled={totpSetupLoading}
                            sx={filledInputSx}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setTotpDialogOpen(false)} disabled={totpSetupLoading}>{t("Cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={handleVerifyTotpSetup}
                        disabled={totpSetupLoading || totpVerifyCode.length !== 6}
                        startIcon={totpSetupLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ bgcolor: arborGreen, color: '#fff' }}
                    >
                        {totpSetupLoading ? t('Verifying...') : t('Verify & Enable')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Disable 2FA Confirmation Dialog */}
            <Dialog
                open={Boolean(disable2faType)}
                onClose={disable2faLoading ? undefined : () => setDisable2faType(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {disable2faType === 'totp' ? t("Disable Authenticator App") : t("Disable Email Verification")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5}>
                        <Typography variant="body2" color="text.secondary">
                            {t("Please enter your account password to confirm disabling two-factor authentication.")}
                        </Typography>
                        <TextField
                            autoFocus
                            label={t("Password")}
                            type="password"
                            variant="filled"
                            fullWidth
                            value={disable2faPassword}
                            onChange={(e) => setDisable2faPassword(e.target.value)}
                            disabled={disable2faLoading}
                            sx={filledInputSx}
                        />
                        {disable2faError && <Alert severity="error">{disable2faError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setDisable2faType(null)} disabled={disable2faLoading}>{t("Cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDisable2fa}
                        disabled={!disable2faPassword || disable2faLoading}
                        startIcon={disable2faLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        color="error"
                    >
                        {disable2faLoading ? t('Disabling...') : t('Disable')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Close Account Dialog */}
            <Dialog
                open={closeAccountOpen}
                onClose={closeAccountLoading ? undefined : () => setCloseAccountOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#401B1B' }}>{t("Close Account")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5}>
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            {t("This will permanently delete your account and all data.")}
                        </Alert>
                        <TextField
                            autoFocus
                            label={t("Enter Password to Confirm")}
                            type="password"
                            variant="filled"
                            fullWidth
                            value={closeAccountPassword}
                            onChange={(e) => setCloseAccountPassword(e.target.value)}
                            disabled={closeAccountLoading}
                            sx={filledInputSx}
                        />
                        {closeAccountError && <Alert severity="error">{closeAccountError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setCloseAccountOpen(false)} disabled={closeAccountLoading}>{t("Cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={handleCloseAccount}
                        disabled={!closeAccountPassword || closeAccountLoading}
                        startIcon={closeAccountLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ bgcolor: '#401B1B', color: '#fff', '&:hover': { bgcolor: '#2d1010' } }}
                    >
                        {closeAccountLoading ? t('Closing...') : t('Permanently Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Profile;
